import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calculator, X } from 'lucide-react';
import { evaluerTokens, formaterResultat, afficherTokens, ERREURS } from '../../utils/calculatrice';

/**
 * Calculatrice collège (type TI-Collège Plus) — FAB bas-GAUCHE (le coup de
 * pouce occupe le bas-droite) + panneau fixe au-dessus. Montée uniquement
 * quand la question autorise la calculatrice (calculatriceAutorisee — flag
 * `contenu.calculatrice` posé par les générateurs). `cosinus` (flag
 * `contenu.calculatrice === 'cos'`, lu par calculatriceCosinus) ajoute une
 * rangée « cos | arccos » au-dessus du clavier : seulement sur les questions
 * qui en ont besoin (4e, 1090), jamais devant un élève de 6e (décision prof
 * 2026-09-24). Le nom « arccos » est celui du cours du prof.
 *
 * État 100 % interne (aucune comptabilité à remonter au parent, contrairement
 * au coup de pouce). Monté SANS key dans Quiz : Ans persiste entre deux
 * questions consécutives autorisées, comme une vraie calculatrice posée sur
 * la table — il se réinitialise quand une question sans flag la démonte.
 *
 * Rendu en PORTAILS (document.body) : insensible aux transforms des ancêtres
 * (slides de carte du quiz). Panneau à zIndex+4 = au-dessus de la sheet coup
 * de pouce (10012) — les deux ouvrables ensemble, zéro couplage.
 */

const CHIFFRES = '0123456789';
const OPERATEURS = ['+', '−', '×', '÷'];
const MAX_TOKENS = 60;

// ── Grille 5×5 esprit TI-Collège : AC/DEL en haut à droite, bloc numérique
// 3×4 à gauche, opérateurs en colonnes de droite, = double en bas à droite ──
const STYLE_CHIFFRE = { background: '#12305a', color: '#fff' };
const STYLE_FONCTION = { background: '#0d2547', color: '#8FD6F5' };
const STYLE_OPERATEUR = { background: '#173a6b', color: '#29B6E8' };
const TOUCHES = [
    { label: '²', token: '²', aria: 'au carré', style: STYLE_FONCTION },
    { label: '√', token: '√(', aria: 'racine carrée', style: STYLE_FONCTION },
    { label: 'π', token: 'π', aria: 'pi', style: STYLE_FONCTION },
    { label: 'DEL', action: 'del', aria: 'effacer le dernier caractère', style: { background: '#FFB938', color: '#3b2303' } },
    { label: 'AC', action: 'ac', aria: 'tout effacer', style: { background: '#F97316', color: '#fff' } },
    { label: '7', token: '7', style: STYLE_CHIFFRE },
    { label: '8', token: '8', style: STYLE_CHIFFRE },
    { label: '9', token: '9', style: STYLE_CHIFFRE },
    { label: '(', token: '(', aria: 'parenthèse ouvrante', style: STYLE_FONCTION },
    { label: ')', token: ')', aria: 'parenthèse fermante', style: STYLE_FONCTION },
    { label: '4', token: '4', style: STYLE_CHIFFRE },
    { label: '5', token: '5', style: STYLE_CHIFFRE },
    { label: '6', token: '6', style: STYLE_CHIFFRE },
    { label: '×', token: '×', aria: 'multiplié', style: STYLE_OPERATEUR },
    { label: '÷', token: '÷', aria: 'divisé', style: STYLE_OPERATEUR },
    { label: '1', token: '1', style: STYLE_CHIFFRE },
    { label: '2', token: '2', style: STYLE_CHIFFRE },
    { label: '3', token: '3', style: STYLE_CHIFFRE },
    { label: '+', token: '+', aria: 'plus', style: STYLE_OPERATEUR },
    { label: '−', token: '−', aria: 'moins', style: STYLE_OPERATEUR },
    { label: '0', token: '0', style: STYLE_CHIFFRE },
    { label: ',', token: ',', aria: 'virgule', style: STYLE_CHIFFRE },
    { label: 'Ans', token: 'Ans', aria: 'dernier résultat', style: STYLE_FONCTION },
    { label: '=', action: 'egal', aria: 'égal', span: 2, style: { background: '#29B6E8', color: '#062033' } },
];
// La rangée du cosinus (degrés), au-dessus du clavier : chaque touche ouvre sa parenthèse, comme √
const TOUCHES_COSINUS = [
    { label: 'cos', token: 'cos(', aria: 'cosinus', style: STYLE_FONCTION },
    { label: 'arccos', token: 'arccos(', aria: 'arc cosinus', style: STYLE_FONCTION },
];
// Après ces tokens, le − est un signe (− unaire), pas une soustraction
const OUVRANTS = ['(', '√(', 'cos(', 'arccos('];

// Le nombre en cours de saisie (fin des tokens) contient-il déjà une virgule ?
const nombreCourantAVirgule = (tokens) => {
    for (let i = tokens.length - 1; i >= 0; i -= 1) {
        const t = tokens[i];
        if (t === ',') return true;
        if (!CHIFFRES.includes(t)) return false;
    }
    return false;
};

// tokens/phase/ans sont COUPLÉS (une frappe peut toucher les trois) : un seul
// objet d'état + updaters PURS — jamais de setState imbriqués (StrictMode).
const ETAT_INITIAL = { tokens: [], ans: 0, phase: 'saisie', resultat: null, erreur: null };

// `decale` : quand le FAB coup de pouce occupe le coin bas-gauche (left:14,
// bottom:18), la calculatrice se range À SA DROITE, de gauche à droite (les
// deux vivent à gauche — à droite ils se confondaient avec « Valider » en
// mobile). Le panneau, lui, reste ancré au bord gauche.
function CalculatriceCollege({ zIndex = 10010, decale = false, cosinus = false }) {
    const gaucheFab = decale ? 88 : 14;
    const [ouverte, setOuverte] = useState(false);
    // phase 'saisie' | 'resultat' | 'erreur' — pilote l'écran et la frappe suivante
    const [etat, setEtat] = useState(ETAT_INITIAL);
    const { tokens, phase, resultat, erreur } = etat;
    const ligneSaisieRef = useRef(null);

    // L'élève voit toujours la FIN de sa saisie (pas de retour à la ligne)
    useEffect(() => {
        const el = ligneSaisieRef.current;
        if (el) el.scrollLeft = el.scrollWidth;
    }, [tokens, phase]);

    const presserToken = useCallback((token) => {
        setEtat((prev) => {
            const estOperateur = OPERATEURS.includes(token) || token === '²';
            // Après = ou une erreur : chiffre/constante repart de zéro,
            // opérateur enchaîne sur Ans (comme la vraie TI)
            const base = prev.phase === 'saisie' ? prev.tokens : (estOperateur ? ['Ans'] : []);
            const repartir = { ...prev, phase: 'saisie', erreur: null };
            if (base.length >= MAX_TOKENS) return { ...repartir, tokens: base };
            const dernier = base[base.length - 1];
            if (token === ',' && nombreCourantAVirgule(base)) return { ...repartir, tokens: base };
            if (OPERATEURS.includes(token)) {
                // − unaire accepté en début et après × ÷ ( √( cos( arccos( ; sinon un
                // opérateur tapé sur un opérateur REMPLACE le précédent
                const unaireOk = token === '−'
                    && (dernier === undefined || dernier === '×' || dernier === '÷' || OUVRANTS.includes(dernier));
                if (!unaireOk) {
                    if (dernier === undefined) return { ...repartir, tokens: base }; // +×÷ en début : refusé
                    if (OPERATEURS.includes(dernier)) return { ...repartir, tokens: [...base.slice(0, -1), token] };
                }
            }
            return { ...repartir, tokens: [...base, token] };
        });
    }, []);

    const presserEgal = useCallback(() => {
        setEtat((prev) => {
            if (prev.phase !== 'saisie' || prev.tokens.length === 0) return prev; // = répété : inerte
            const { valeur, erreur: code } = evaluerTokens(prev.tokens, prev.ans);
            if (code) return { ...prev, phase: 'erreur', erreur: code };
            return { ...prev, phase: 'resultat', resultat: valeur, ans: valeur };
        });
    }, []);

    const presserDel = useCallback(() => {
        // En résultat/erreur, la 1re pression REPREND l'expression sans effacer
        setEtat((prev) => (prev.phase === 'saisie'
            ? { ...prev, tokens: prev.tokens.slice(0, -1), erreur: null }
            : { ...prev, phase: 'saisie', erreur: null }));
    }, []);

    const presserAc = useCallback(() => {
        // Tout effacer SAUF Ans (comme la TI)
        setEtat((prev) => ({ ...prev, tokens: [], resultat: null, erreur: null, phase: 'saisie' }));
    }, []);

    const presserTouche = useCallback((touche) => {
        if (touche.action === 'egal') presserEgal();
        else if (touche.action === 'del') presserDel();
        else if (touche.action === 'ac') presserAc();
        else presserToken(touche.token);
    }, [presserEgal, presserDel, presserAc, presserToken]);

    // ── Clavier physique (panneau ouvert seulement) ──
    useEffect(() => {
        if (!ouverte) return undefined;
        const surTouche = (e) => {
            // Le focus dans un champ de réponse prime : la calculatrice n'y touche pas
            const cible = e.target;
            if (cible && (cible.tagName === 'INPUT' || cible.tagName === 'TEXTAREA' || cible.isContentEditable)) return;
            const map = {
                ',': ',', '.': ',', '+': '+', '-': '−', '*': '×', '/': '÷', '(': '(', ')': ')',
            };
            let traite = true;
            if (CHIFFRES.includes(e.key)) presserToken(e.key);
            else if (map[e.key]) presserToken(map[e.key]);
            else if (e.key === 'Enter' || e.key === '=') presserEgal();
            else if (e.key === 'Backspace') presserDel();
            else if (e.key === 'Delete') presserAc();
            else if (e.key === 'Escape') setOuverte(false);
            else traite = false;
            if (traite) e.preventDefault();
        };
        window.addEventListener('keydown', surTouche);
        return () => window.removeEventListener('keydown', surTouche);
    }, [ouverte, presserToken, presserEgal, presserDel, presserAc]);

    // ── Écran : ligne expression (petite) + ligne saisie/résultat (grande) ──
    const ligneExpression = phase === 'saisie' ? '' : afficherTokens(tokens);
    let ligneBas;
    if (phase === 'erreur') ligneBas = <span className="text-sm font-semibold" style={{ color: '#FFB938' }}>{ERREURS[erreur]}</span>;
    else if (phase === 'resultat') {
        const texte = formaterResultat(resultat);
        const [mantisse, exposant] = texte.split('×10^');
        ligneBas = exposant !== undefined
            ? <span>{mantisse}×10<sup>{exposant}</sup></span>
            : <span>{texte}</span>;
    } else ligneBas = <span>{tokens.length ? afficherTokens(tokens) : '0'}</span>;

    const fab = createPortal(
        <button
            type="button"
            onClick={() => setOuverte((o) => !o)}
            aria-label={ouverte ? 'Fermer la calculatrice' : 'Ouvrir la calculatrice'}
            aria-expanded={ouverte}
            className="eleve-gamified fixed flex flex-col items-center transition-transform active:scale-95"
            style={{ left: gaucheFab, bottom: 18, zIndex }}
        >
            <span
                className="relative flex items-center justify-center rounded-full shadow-xl"
                style={{
                    width: 58, height: 58,
                    background: 'radial-gradient(circle at 30% 25%, #1b3f68, #0A1F3D)',
                    border: '2.5px solid #29B6E8',
                }}
            >
                <Calculator className="w-7 h-7" style={{ color: '#8FD6F5' }} />
            </span>
            <span
                className="mt-1 text-[9px] font-extrabold px-2 py-px rounded-full"
                style={{ fontFamily: 'Fredoka', background: 'rgba(10,31,61,.85)', color: '#8FD6F5', border: '1px solid rgba(255,255,255,.15)' }}
            >
                Calc
            </span>
        </button>,
        document.body,
    );

    const panneau = ouverte ? createPortal(
        <div
            className="eleve-gamified fixed rounded-2xl shadow-2xl p-3"
            role="dialog"
            aria-label="Calculatrice"
            style={{
                left: 8, bottom: 84, zIndex: zIndex + 4,
                width: 'min(300px, calc(100vw - 16px))',
                background: 'linear-gradient(160deg, #0A1F3D, #1D3A66)',
                border: '1px solid rgba(41,182,232,.45)',
                fontFamily: 'Fredoka',
            }}
        >
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold" style={{ color: '#8FD6F5' }}>Calculatrice</span>
                <button
                    type="button"
                    onClick={() => setOuverte(false)}
                    aria-label="Fermer la calculatrice"
                    className="flex items-center justify-center rounded-full transition active:scale-90"
                    style={{ width: 24, height: 24, background: 'rgba(41,182,232,.15)', color: '#8FD6F5' }}
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>

            <div className="rounded-xl px-3 py-2 mb-2" style={{ background: '#061426' }}>
                <div
                    className="text-xs text-right whitespace-nowrap overflow-x-auto"
                    style={{ color: 'rgba(143,214,245,.7)', minHeight: 16, scrollbarWidth: 'none' }}
                >
                    {ligneExpression || ' '}
                </div>
                <div
                    ref={ligneSaisieRef}
                    className="text-2xl font-semibold text-right text-white whitespace-nowrap overflow-x-auto tabular-nums"
                    aria-live="polite"
                    style={{ minHeight: 32, scrollbarWidth: 'none' }}
                >
                    {ligneBas}
                </div>
            </div>

            {cosinus ? (
                <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                    {TOUCHES_COSINUS.map((touche) => (
                        <button
                            key={touche.label}
                            type="button"
                            onClick={() => presserTouche(touche)}
                            aria-label={touche.aria}
                            className="h-10 rounded-lg font-bold text-base transition select-none active:scale-95"
                            style={{ ...touche.style, fontFamily: 'Fredoka' }}
                        >
                            {touche.label}
                        </button>
                    ))}
                </div>
            ) : null}

            <div className="grid grid-cols-5 gap-1.5">
                {TOUCHES.map((touche) => (
                    <button
                        key={touche.label}
                        type="button"
                        onClick={() => presserTouche(touche)}
                        aria-label={touche.aria || touche.label}
                        className={`h-10 rounded-lg font-bold text-base transition select-none active:scale-95${touche.span === 2 ? ' col-span-2' : ''}`}
                        style={{ ...touche.style, fontFamily: 'Fredoka' }}
                    >
                        {touche.label}
                    </button>
                ))}
            </div>
        </div>,
        document.body,
    ) : null;

    return (
        <>
            {fab}
            {panneau}
        </>
    );
}

export default CalculatriceCollege;
